/* eslint-disable indent */
/* eslint-disable react/jsx-indent */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { toggleExpandedPath } from '../../../../redux/providerBuilder/jsonViewer';
import { getExpandedPaths } from '../../../../redux/providerBuilder/jsonViewer/selectors';
import { resolveJsonPathStyle } from 'src/utils/provider-builder/instance-selection-utils';

interface OptimizedJsonViewerProps {
  data: any;
  onItemClick: any;
  searchTerm: string;
  isDisabled: boolean;
}

const OptimizedJsonViewer: React.FC<OptimizedJsonViewerProps> = ({
  data,
  onItemClick,
  searchTerm,
  isDisabled,
}) => {
  const dispatch = useAppDispatch();
  const expandedPaths = useAppSelector(getExpandedPaths);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [selectedKey, setSelectedKey] = useState<{
    path: string;
    key: string;
  } | null>(null);
  const [isUniqueToUser, setIsUniqueToUser] = useState(false);
  const [isPrivateInformation, setIsPrivateInformation] = useState(false);
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleKeyClick = useCallback(
    (event: React.MouseEvent, path: string, key: string) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX,
      });
      setSelectedKey({ path, key });
      onOpen();
    },
    [onOpen]
  );

  const handleAddClick = useCallback(() => {
    setIsLoading(true);

    if (selectedKey) {
      onItemClick(
        selectedKey.path,
        selectedKey.key,
        isUniqueToUser,
        isPrivateInformation,
        description
      );
    }

    onClose();
    setSelectedKey(null);
    setIsUniqueToUser(false);
    setIsPrivateInformation(false);
    setDescription('');
    setIsLoading(false);
  }, [selectedKey, isUniqueToUser, isPrivateInformation, onClose, description]);

  const toggleCollapse = useCallback(
    (path: string) => {
      dispatch(toggleExpandedPath(path));
    },
    [dispatch]
  );

  const highlightText = useCallback(
    (text: string) => {
      if (!searchTerm) {
        return text;
      }

      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
      const parts = text.split(regex);

      return parts.map((part, index) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <span
            key={index}
            className="highlight-searched-json-value"
            style={{ backgroundColor: 'yellow' }}
          >
            {part}
          </span>
        ) : (
          part
        )
      );
    },
    [searchTerm]
  );

  const renderValue = useCallback(
    (value: any, path: string, key: string) => {
      const commonStyles = {
        cursor: 'pointer',
        className: 'font-roboto medium-font',
        style: {
          fontWeight: 300,
          display: 'inline-block',
          maxWidth: '500px',
          whiteSpace: 'normal',
        },
        // onClick: (e: React.MouseEvent) => handleKeyClick(e, path, key),
        onClick: (e: React.MouseEvent) =>
          handleKeyClick(e, resolveJsonPathStyle(path, data), key),
      };

      if (value === null) {
        return <Text {...commonStyles}>null</Text>;
      }

      if (typeof value === 'boolean') {
        return <Text {...commonStyles}>{highlightText(String(value))}</Text>;
      }

      if (typeof value === 'number') {
        return <Text {...commonStyles}>{highlightText(String(value))}</Text>;
      }

      if (typeof value === 'string') {
        return (
          <Text {...commonStyles}>
            &quot;
            {highlightText(value)}
            &quot;
          </Text>
        );
      }

      return renderObject(value, path, key, expandedPaths?.[path] ?? true);
    },
    [expandedPaths, highlightText]
  );

  const renderObject = useCallback(
    (obj: any, path: string, key: string, isExpanded: boolean) => {
      const isArray = Array.isArray(obj);

      return (
        <Flex
          ml={0}
          direction={isExpanded ? 'column' : 'row'}
          alignItems={isExpanded ? 'flex-start' : 'center'}
        >
          <Flex alignItems="center">
            <Text
              color="black"
              cursor="pointer"
              onClick={() => onItemClick(path, key)}
              className="medium-font"
            >
              {isArray ? '[' : '{'}
            </Text>
            <span
              className="toggle-button"
              onClick={() => !isDisabled && toggleCollapse(path)}
            >
              {isExpanded ? <span>&#8854;</span> : <span>&#8853;</span>}
            </span>
          </Flex>
          {isExpanded && (
            <Box ml={4} position="relative">
              <Box
                position="absolute"
                left="-10px"
                top="5px"
                bottom="0px"
                height="calc(100% - 10px)"
                borderLeft="1px solid #ccc"
              />
              {Object.entries(obj).map(([k, v], index) => (
                <Flex key={k} alignItems="flex-start" direction={'column'} ml={3} mb={2}>
                  <Text
                    ref={triggerRef}
                    cursor="pointer"
                    fontWeight={900}
                    // onClick={(e) => handleKeyClick(e, `${path}.${k}`, k)}
                    onClick={(e) =>
                      handleKeyClick(
                        e,
                        resolveJsonPathStyle(`${path ? `${path}.` : ''}${k}`, data),
                        k
                      )
                    }
                  >
                    <Flex alignItems="center">
                      {isArray ? (
                        <span className="xx-small-font" style={{ marginTop: '1px' }}>
                          {index}
                        </span>
                      ) : (
                        <>
                          <span className="json-key-quotes medium-font">&quot;</span>
                          <span
                            className="json-key medium-font"
                            style={{ textDecoration: 'underline' }}
                          >
                            {k}
                          </span>
                          <span className="json-key-quotes medium-font">&quot;</span>
                        </>
                      )}
                      <span style={{ marginLeft: '5px' }}>:</span>
                    </Flex>
                  </Text>
                  <Box ml={2}>
                    {renderValue(v, `${path}${isArray ? `[${k}]` : `.${k}`}`, k)}
                  </Box>
                </Flex>
              ))}
            </Box>
          )}
          <Text color="black" ml={1} className="medium-font">
            {isArray ? ']' : '}'}
          </Text>
        </Flex>
      );
    },
    [onItemClick, toggleCollapse, renderValue, highlightText]
  );

  const memoizedContent = useMemo(() => {
    return renderObject(data, '', 'root', expandedPaths?.[''] ?? true);
  }, [data, renderObject, expandedPaths, searchTerm]);

  return (
    <Box fontSize="sm" position="relative">
      {memoizedContent}
      <Portal>
        <Popover
          isOpen={isOpen}
          onClose={onClose}
          placement="right"
          closeOnBlur={false}
          returnFocusOnClose={false}
          initialFocusRef={triggerRef}
          arrowSize={15}
        >
          <PopoverContent
            position="absolute"
            top={`calc(${popoverPosition.top}px - 50px)`}
            left={`${popoverPosition.left}px`}
            borderRadius={10}
            border="2px dashed black"
            w="400px"
            pt={2}
          >
            <PopoverArrow border="2px dashed black" />
            <PopoverCloseButton
              zIndex={10}
              fontSize="x-small"
              color="black"
              fontWeight="bolder"
            />
            <PopoverBody bgColor="white" borderRadius={15}>
              <Flex direction="column">
                <Flex direction="column" w="90%" gap={1}>
                  <Flex
                    gap={1}
                    mb={2}
                    px={2}
                    py={1}
                    borderRadius={5}
                    alignItems="center"
                    border="1px solid #ccc"
                  >
                    {/* <MdTextFields
                                            color='black'
                                            fontSize='25px' /> */}
                    <Input
                      variant="unstyled"
                      placeholder="Enter Description..."
                      pl={2}
                      color="gray"
                      fontWeight={400}
                      fontSize="clamp(12px, calc(var(--base-font-size) * 1.8), 22px)"
                      fontFamily="inter"
                      letterSpacing={1}
                      _placeholder={{
                        color: '#8c8c8c',
                        fontSize: 'clamp(12px, calc(var(--base-font-size) * 1.8), 22px)',
                      }}
                      borderRadius={0}
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                      }}
                    />
                  </Flex>
                  <Flex gap={3} ml={1}>
                    <Checkbox
                      iconColor={isUniqueToUser ? 'white' : ''}
                      bgColor={isUniqueToUser ? 'black' : ''}
                      borderRadius="5px"
                      size="lg"
                      colorScheme="blackAlpha"
                      isChecked={isUniqueToUser}
                      h="full"
                      onChange={() => setIsUniqueToUser(!isUniqueToUser)}
                    />
                    <Text
                      color="gray.600"
                      fontWeight={400}
                      className="regular-font"
                      fontFamily="inter"
                      letterSpacing={1}
                    >
                      Unique to user
                    </Text>
                  </Flex>
                  <Flex gap={3} ml={1}>
                    <Checkbox
                      iconColor={isPrivateInformation ? 'white' : ''}
                      bgColor={isPrivateInformation ? 'black' : ''}
                      borderRadius="5px"
                      size="lg"
                      colorScheme="blackAlpha"
                      isChecked={isPrivateInformation}
                      h="full"
                      onChange={() => setIsPrivateInformation(!isPrivateInformation)}
                    />
                    <Text
                      color="gray.600"
                      fontWeight={400}
                      className="regular-font"
                      fontFamily="inter"
                      letterSpacing={1}
                    >
                      Private information
                    </Text>
                  </Flex>
                </Flex>
                <Flex justifyContent="flex-end">
                  <Button
                    size="sm"
                    bgColor="black"
                    color="white"
                    onClick={handleAddClick}
                    my={2}
                    borderRadius={10}
                    isLoading={isLoading}
                    isDisabled={!description}
                  >
                    Add
                  </Button>
                </Flex>
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Portal>
    </Box>
  );
};

export default OptimizedJsonViewer;
